import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import styles from "./Paginate.module.css";

const Paginate = ({
  currentPage,
  totalPages,
  onPageChange,
  totalData,
  itemTitle,
  hideText = null,
  pageSize,
  currentCount,
  showRange = true,
  previousLabel = "‹ Previous",
  nextLabel = "Next ›",
}) => {
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const generatePageNumbers = () => {
    const pageNumbers = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 4) {
        // Show first 5 pages + ellipsis + last page
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage > totalPages - 4) {
        // Show first page + ellipsis + last 5 pages
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Show first + ellipsis + current range + ellipsis + last
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const pages = generatePageNumbers();

  const safePage = Math.max(currentPage ?? 1, 1);
  const size = pageSize && pageSize > 0 ? pageSize : undefined;
  const previousRecords = size
    ? (safePage - 1) * size
    : currentCount && safePage > 1
    ? (safePage - 1) * currentCount
    : 0;
  const visibleOnPage = typeof currentCount === "number"
    ? currentCount
    : size
    ? Math.min(size, Math.max(totalData - previousRecords, 0))
    : totalData;
  const displayCount = Math.min(previousRecords + visibleOnPage, totalData);

  const indexOfLastData = currentPage * (size || 10);
  const indexOfFirstData = indexOfLastData - (size || 10);
  const rangeStart = totalData === 0 ? 0 : indexOfFirstData + 1;
  const rangeEnd = indexOfLastData < totalData ? indexOfLastData : totalData;

  // Determine the label to show (entries or custom itemTitle with singular/plural)
  const getDisplayLabel = () => {
    if (itemTitle) {
      // If itemTitle is provided, use it (caller should handle singular/plural)
      return itemTitle;
    }
    return totalData === 1 ? 'entry' : 'entries';
  };

  return (
    <div className={`${styles.paginationContainer} ${styles.showingMain} p-0 my-3`}>
      <div className={`d-flex ${showRange ? 'justify-content-between' : 'justify-content-center'} align-items-center flex-wrap w-100`}>
        {showRange && (
          <div className={styles.showingPara}>
            <p>Displaying {rangeStart} to {rangeEnd} of {totalData} {getDisplayLabel()}</p>
          </div>
        )}
        <div className={`${styles.paginationButtons} d-flex`}>
          <button
            type="button"
            className={styles.previousBtn}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {previousLabel}
          </button>

          {pages.map((page, index) => {
            if (page === "...") {
              return (
                <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                  ...
                </span>
              );
            }
            return (
              <button
                key={page}
                type="button"
                className={`${styles.pageNum} ${currentPage === page ? styles.pageActive : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            );
          })}

          <button
            type="button"
            className={styles.nextBtn}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Paginate;
